/**
 * パスワードの規則（2026-10-26、EV-474）。管理画面の判定はすべてここを使う。
 * アプリの規則（apps/mobile/lib/shared/utils/password_rules.dart、#401）と同じ。変えるときは両方とサーバーを同時に変える。
 *
 * サーバーの規則（services/backend/yometel_alcohol_check_backend/settings.py の AUTH_PASSWORD_VALIDATORS）:
 *   - MinimumLengthValidator（既定の 8 文字。Python の len、つまりコードポイントの数）
 *   - SymbolValidator（validators.py）。記号 `[!@#$%^&*(),.?":{}|<>]` のどれか 1 つ
 *   - CommonPasswordValidator・NumericPasswordValidator・UserAttributeSimilarityValidator
 *     （辞書・利用者の情報が要るため、管理画面では判定しない。断られたらサーバーの文を出す）
 * 管理画面はこれに加えて、大文字・小文字・数字をそれぞれ 1 つ以上求める（画面の説明の文のとおり）。
 *
 * 以前の管理画面（passwordStrength.tsx の点数。8文字以上 +1／未満 −1、数字 +1、大文字 +1、ASCII の記号 +1 の
 * 合計が3以上で送る）は、サーバーの規則と食い違っていた。`abcdefg#` を断り（サーバーは通す）、`Abcdef-1` を通した
 * （`-` はサーバーの記号ではないので、サーバーが断る）。
 *
 * 前後の空白・改行（normalize）: サーバーの password の欄（再設定・初回の変更の Serializer、ログインの simplejwt）は
 * DRF の CharField（trim_whitespace=True）で、Python の str.strip() で前後を取り除いてから判定・保存・照合する。
 * 管理画面も同じ範囲の文字を取り除いて判定し、取り除いた値を送る。
 *
 * 途中の見えない文字（invisible）と全角（halfWidth）は管理画面だけの条件。サーバーは受け付けるが、利用者には
 * 見分けられない。入力を勝手に直さず、満たしていないと理由を示す。
 */

export type PasswordRequirement =
  | 'invisible'
  | 'halfWidth'
  | 'minLength'
  | 'lowercase'
  | 'uppercase'
  | 'digit'
  | 'symbol';

/** 画面の並び（見えない文字・半角・長さ・小文字・大文字・数字・記号）。 */
export const PASSWORD_REQUIREMENTS: readonly PasswordRequirement[] = [
  'invisible',
  'halfWidth',
  'minLength',
  'lowercase',
  'uppercase',
  'digit',
  'symbol',
];

/** サーバーの MinimumLengthValidator の既定と同じ。 */
export const PASSWORD_MIN_LENGTH = 8;

/** サーバーの SymbolValidator の集合を写したもの。 */
export const PASSWORD_SYMBOLS = '!@#$%^&*(),.?":{}|<>';

const SYMBOL = /[!@#$%^&*(),.?":{}|<>]/;
const LOWERCASE = /[a-z]/;
const UPPERCASE = /[A-Z]/;
const DIGIT = /[0-9]/;

/**
 * Python の str.strip() が取り除く文字（str.isspace() が真の文字）。JavaScript の String.prototype.trim() とは
 * 違う（trim は U+FEFF を取り除き、U+001C〜U+001F・U+0085 を残す）ので、サーバーと同じ集合を書く。
 */
const PYTHON_SPACE =
  '\\t\\n\\x0B\\f\\r\\x1C-\\x1F \\x85\\xA0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000';
// eslint-disable-next-line no-control-regex
const EDGES = new RegExp(`^[${PYTHON_SPACE}]+|[${PYTHON_SPACE}]+$`, 'g');

/** 見えない文字。制御文字・空白（半角の空白を含む）・ゼロ幅の文字などの書式の文字・ソフトハイフン。 */
// eslint-disable-next-line no-control-regex
const INVISIBLE = /[\x00-\x20\x7F-\xA0\xAD\u1680\u180E\u2000-\u200F\u2028-\u202F\u205F-\u2064\u2066-\u206F\u3000\uFEFF]/;
// eslint-disable-next-line no-control-regex
const INVISIBLE_ALL = new RegExp(INVISIBLE.source, 'g');
const NOT_HALF_WIDTH = /[^\x21-\x7E]/u;

/** サーバーと同じく、前後の空白・改行を取り除いた値。判定と送信はこの値で行う。 */
export function normalizePassword(value: string): string {
  return value.replace(EDGES, '');
}

/** サーバー（Python の len）と同じく、コードポイントで数える。 */
function codePoints(value: string): number {
  return Array.from(value).length;
}

/** 満たしていない条件（PASSWORD_REQUIREMENTS の順）。 */
export function missingPasswordRequirements(value: string): PasswordRequirement[] {
  const password = normalizePassword(value);
  const checks: Record<PasswordRequirement, boolean> = {
    invisible: !INVISIBLE.test(password),
    halfWidth: !NOT_HALF_WIDTH.test(password.replace(INVISIBLE_ALL, '')),
    minLength: codePoints(password) >= PASSWORD_MIN_LENGTH,
    lowercase: LOWERCASE.test(password),
    uppercase: UPPERCASE.test(password),
    digit: DIGIT.test(password),
    symbol: SYMBOL.test(password),
  };
  return PASSWORD_REQUIREMENTS.filter((requirement) => !checks[requirement]);
}

export function isPasswordSatisfied(value: string): boolean {
  return missingPasswordRequirements(value).length === 0;
}

/** 確認用と一致する（どちらも前後を取り除いて比べる。サーバーに届く値が同じなら一致）。 */
export function passwordsMatch(value: string, confirm: string): boolean {
  return confirm !== '' && normalizePassword(value) === normalizePassword(confirm);
}

/**
 * 強さの段階（0〜4）。規則と食い違わないよう、規則をすべて満たしたときだけ 4（いちばん強い）にする。
 * アプリの PasswordStrength.levelOf と同じ。
 */
export function passwordLevel(value: string): number {
  const missing = missingPasswordRequirements(value);
  if (missing.includes('minLength')) return 0;
  if (missing.length === 0) return 4;
  const hasUpper = !missing.includes('uppercase');
  const hasDigit = !missing.includes('digit');
  const hasSymbol = !missing.includes('symbol');
  if (hasUpper && (hasDigit || hasSymbol)) return 3;
  if (hasUpper || hasDigit) return 2;
  return 1;
}
